/**
 * The bearing dial — a code-drawn compass in the logo's green|blue split.
 * Two tick rings turn very slowly in opposite directions and the needle
 * sweeps; all of it stops under prefers-reduced-motion. Coordinates are
 * rounded so server/client attribute strings never differ.
 */
const r2 = (n: number) => Math.round(n * 100) / 100;

function ticks(count: number, rOuter: number, longEvery: number, longLen: number, shortLen: number) {
  return Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2;
    const len = i % longEvery === 0 ? longLen : shortLen;
    const x1 = 200 + Math.sin(a) * rOuter;
    const y1 = 200 - Math.cos(a) * rOuter;
    const x2 = 200 + Math.sin(a) * (rOuter - len);
    const y2 = 200 - Math.cos(a) * (rOuter - len);
    return { x1: r2(x1), y1: r2(y1), x2: r2(x2), y2: r2(y2), long: i % longEvery === 0 };
  });
}

const OUTER = ticks(120, 188, 10, 12, 5);
const INNER = ticks(72, 150, 6, 8, 3);
const BEARINGS = [
  { t: "N", a: 0 }, { t: "E", a: 90 }, { t: "S", a: 180 },
].map((b) => ({
  ...b,
  x: r2(200 + Math.sin((b.a * Math.PI) / 180) * 122),
  y: r2(200 - Math.cos((b.a * Math.PI) / 180) * 122 + 4),
}));

export function CompassDial({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 400 400" className={className} aria-hidden>
      <defs>
        <linearGradient id="wm-split" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0.5" stopColor="#08B9A8" />
          <stop offset="0.5" stopColor="#0F7DF0" />
        </linearGradient>
        <linearGradient id="wm-fade" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#08B9A8" />
          <stop offset="1" stopColor="#0F7DF0" />
        </linearGradient>
        <radialGradient id="wm-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#0BACD4" stopOpacity="0.45" />
          <stop offset="1" stopColor="#0BACD4" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="200" cy="200" r="200" fill="url(#wm-glow)" />
      <circle cx="200" cy="200" r="190" fill="none" stroke="#C0C6D4" strokeOpacity="0.14" />
      <circle cx="200" cy="200" r="152" fill="none" stroke="#C0C6D4" strokeOpacity="0.10" />
      <circle cx="200" cy="200" r="96" fill="none" stroke="url(#wm-fade)" strokeOpacity="0.35" strokeDasharray="2 6" />

      <g className="wm-rotate-slow">
        {OUTER.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={t.long ? "url(#wm-fade)" : "#C0C6D4"} strokeOpacity={t.long ? 0.9 : 0.25} strokeWidth={t.long ? 1.6 : 1} />
        ))}
      </g>
      <g className="wm-rotate-rev">
        {INNER.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke="#C0C6D4" strokeOpacity={t.long ? 0.5 : 0.18} strokeWidth={1} />
        ))}
      </g>

      {BEARINGS.map((b) => (
        <text key={b.t} x={b.x} y={b.y} textAnchor="middle" fontFamily="Montserrat, sans-serif" fontWeight={700}
          fontSize="13" letterSpacing="1" fill="#C0C6D4" fillOpacity={b.t === "N" ? 0.95 : 0.45}>
          {b.t}
        </text>
      ))}

      {/* needle: north half split green|blue like the logo's W */}
      <g className="wm-needle">
        <path d="M200 70 L214 200 L186 200 Z" fill="url(#wm-split)" />
        <path d="M200 330 L214 200 L186 200 Z" fill="#C0C6D4" fillOpacity="0.22" />
        <circle cx="200" cy="200" r="9" fill="#10243A" stroke="url(#wm-fade)" strokeWidth="2.5" />
      </g>
    </svg>
  );
}
